import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Send, MessageSquare, Zap, BarChart3, Users, CheckCircle2, ArrowRight } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-brand grid place-items-center text-white">
              <Send className="w-4 h-4" />
            </div>
            <span className="font-heading text-lg font-semibold text-slate-900">FlowSend</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" data-testid="landing-login">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button asChild className="bg-brand hover:bg-brand-dark" data-testid="landing-signup">
              <Link to="/signup">Start free</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-brand-dark bg-brand-light px-3 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Lightweight alternative to AiSensy, Interakt & WATI
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mt-5">
            WhatsApp automation for solo founders & small teams.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl">
            Broadcasts, drip flows, inbox, tags and analytics — built for the operator who ships fast and doesn't need a 30-seat enterprise plan.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Button asChild size="lg" className="bg-brand hover:bg-brand-dark" data-testid="landing-cta-primary">
              <Link to="/signup">Start free <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
            <Button asChild size="lg" variant="ghost" data-testid="landing-cta-secondary">
              <Link to="/login">I have an account</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Send, title: "Broadcasts", desc: "Send WhatsApp campaigns to thousands. Schedule, segment, track delivery & replies." },
            { icon: Zap, title: "No-code Flows", desc: "Drag-and-drop visual builder. Triggers, conditions, delays, templates and tags." },
            { icon: MessageSquare, title: "Unified Inbox", desc: "Every conversation in one place. Reply fast, view timeline, add notes." },
            { icon: Users, title: "Smart CRM", desc: "Tag, segment and filter contacts. Custom fields, lead statuses, CSV import/export." },
            { icon: BarChart3, title: "Real Analytics", desc: "Conversion funnels, broadcast performance, contact growth — straight to the point." },
            { icon: CheckCircle2, title: "Meta Cloud API", desc: "Production-ready WhatsApp Cloud API integration. Webhook-driven event processing." },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="p-6 border border-slate-200 rounded-lg">
                <div className="w-10 h-10 rounded-md bg-slate-100 text-slate-700 grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading font-semibold text-slate-900 text-lg">{f.title}</div>
                <p className="text-sm text-slate-500 mt-1">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        © 2026 FlowSend. Built for operators.
      </footer>
    </div>
  );
}
